import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TemplateModal = ({ language, isOpen, onClose, onGenerate }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [templateLanguage, setTemplateLanguage] = useState('fr');
  const [clientName, setClientName] = useState('');
  const [caseNumber, setCaseNumber] = useState('');

  const templates = [
    {
      value: 'employment-contract',
      labelEn: 'Employment Contract',
      labelFr: 'Contrat de travail',
      labelAr: 'عقد العمل',
      descriptionEn: 'Standard employment agreement template',
      descriptionFr: 'Modèle standard de contrat de travail',
      descriptionAr: 'نموذج عقد العمل القياسي',
      icon: 'FileText'
    },
    {
      value: 'lease-agreement',
      labelEn: 'Lease Agreement',
      labelFr: 'Contrat de bail',
      labelAr: 'عقد الإيجار',
      descriptionEn: 'Commercial or residential lease template',
      descriptionFr: 'Modèle de bail commercial ou résidentiel',
      descriptionAr: 'نموذج الإيجار التجاري أو السكني',
      icon: 'Home'
    },
    {
      value: 'power-attorney',
      labelEn: 'Power of Attorney',
      labelFr: 'Procuration',
      labelAr: 'توكيل رسمي',
      descriptionEn: 'Legal authorization document',
      descriptionFr: 'Document d\'autorisation légale',
      descriptionAr: 'وثيقة التفويض القانوني',
      icon: 'FileSignature'
    },
    {
      value: 'demand-letter',
      labelEn: 'Demand Letter',
      labelFr: 'Lettre de mise en demeure',
      labelAr: 'خطاب المطالبة',
      descriptionEn: 'Formal demand for payment or action',
      descriptionFr: 'Demande formelle de paiement ou d\'action',
      descriptionAr: 'مطالبة رسمية بالدفع أو الإجراء',
      icon: 'Mail'
    },
    {
      value: 'court-pleading',
      labelEn: 'Court Pleading',
      labelFr: 'Plaidoirie',
      labelAr: 'مرافعة المحكمة',
      descriptionEn: 'Court submission document',
      descriptionFr: 'Document de soumission au tribunal',
      descriptionAr: 'وثيقة تقديم المحكمة',
      icon: 'Scale'
    },
    {
      value: 'settlement-agreement',
      labelEn: 'Settlement Agreement',
      labelFr: 'Accord de règlement',
      labelAr: 'اتفاقية التسوية',
      descriptionEn: 'Dispute resolution agreement',
      descriptionFr: 'Accord de résolution de litige',
      descriptionAr: 'اتفاقية حل النزاع',
      icon: 'Handshake'
    }
  ];

  const languageOptions = [
    { value: 'fr', label: 'Français' },
    { value: 'ar', label: 'العربية' },
    { value: 'both', label: language === 'fr' ? 'Bilingue (FR/AR)' : language === 'ar' ? 'ثنائي اللغة (FR/AR)' : 'Bilingual (FR/AR)' }
  ];

  const titleLabel = {
    en: 'Generate Legal Document',
    fr: 'Générer un document juridique',
    ar: 'إنشاء مستند قانوني'
  };

  const templateLabel = {
    en: 'Select Template',
    fr: 'Sélectionner un modèle',
    ar: 'اختر النموذج'
  };

  const languageLabel = {
    en: 'Document Language',
    fr: 'Langue du document',
    ar: 'لغة المستند'
  };

  const clientLabel = {
    en: 'Client Name',
    fr: 'Nom du client',
    ar: 'اسم العميل'
  };

  const caseLabel = {
    en: 'Case Number (Optional)',
    fr: 'Numéro de dossier (Optionnel)',
    ar: 'رقم القضية (اختياري)'
  };

  const generateLabel = {
    en: 'Generate Document',
    fr: 'Générer le document',
    ar: 'إنشاء المستند'
  };

  const cancelLabel = {
    en: 'Cancel',
    fr: 'Annuler',
    ar: 'إلغاء'
  };

  const getTemplateLabel = (template) => {
    if (language === 'fr') return template?.labelFr;
    if (language === 'ar') return template?.labelAr;
    return template?.labelEn;
  };

  const getTemplateDescription = (template) => {
    if (language === 'fr') return template?.descriptionFr;
    if (language === 'ar') return template?.descriptionAr;
    return template?.descriptionEn;
  };

  const handleGenerate = () => {
    if (selectedTemplate && clientName) {
      onGenerate({
        template: selectedTemplate,
        language: templateLanguage,
        clientName,
        caseNumber
      });
      setSelectedTemplate('');
      setTemplateLanguage('fr');
      setClientName('');
      setCaseNumber('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-elevation-lg w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {titleLabel?.[language]}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div>
            <label className="text-sm font-medium text-foreground mb-3 block">
              {templateLabel?.[language]}
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {templates?.map((template) => (
                <div
                  key={template?.value}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedTemplate === template?.value
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                  }`}
                  onClick={() => setSelectedTemplate(template?.value)}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name={template?.icon} size={20} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-foreground mb-1">
                        {getTemplateLabel(template)}
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        {getTemplateDescription(template)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Select
            label={languageLabel?.[language]}
            options={languageOptions}
            value={templateLanguage}
            onChange={setTemplateLanguage}
            required
          />

          <Input
            label={clientLabel?.[language]}
            type="text"
            placeholder={language === 'fr' ? 'Entrez le nom du client' : language === 'ar' ? 'أدخل اسم العميل' : 'Enter client name'}
            value={clientName}
            onChange={(e) => setClientName(e?.target?.value)}
            required
          />

          <Input
            label={caseLabel?.[language]}
            type="text"
            placeholder={language === 'fr' ? 'Ex: 2024-156' : language === 'ar' ? 'مثال: 2024-156' : 'e.g., 2024-156'}
            value={caseNumber}
            onChange={(e) => setCaseNumber(e?.target?.value)}
          />
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            {cancelLabel?.[language]}
          </Button>
          <Button
            variant="default"
            onClick={handleGenerate}
            disabled={!selectedTemplate || !clientName}
            iconName="FileText"
            iconPosition="left"
          >
            {generateLabel?.[language]}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TemplateModal;